import { countdownTarget } from '@content-scheduler/shared/render';
import { linkProps } from '../links';
import { Buttons } from '../parts/Buttons';
import { Countdown } from '../parts/Countdown';
import { CardLayout, OverlayLayout, SplitLayout, layouts } from '../parts/Layouts';
import parts from '../parts/parts.module.css';
import type { TemplateProps } from '../types';
import styles from './event.module.css';
import { formatEventDate } from './eventDate';

const ClockIcon = () => (
    <svg viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path
            d="M8 4.75V8l2.25 1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
);

const PinIcon = () => (
    <svg viewBox="0 0 16 16" aria-hidden="true">
        <path
            d="M8 14.5s4.75-4.2 4.75-8A4.75 4.75 0 0 0 3.25 6.5c0 3.8 4.75 8 4.75 8z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
        />
        <circle cx="8" cy="6.5" r="1.6" fill="currentColor" />
    </svg>
);

export function Event({ doc, ctx, onDismiss }: TemplateProps<'event'>) {
    const { eyebrow, headline, body, date, location, mapUrl, buttons, image, countdown } =
        doc.fields;
    const when = formatEventDate(date);
    const target = countdownTarget(doc, ctx.schedule);
    const place = location.trim();

    const content = (
        <>
            {eyebrow.trim() && <p className={parts.eyebrow}>{eyebrow}</p>}
            <h2 className={parts.headline}>{headline}</h2>
            {(when || place) && (
                <div className={styles.details}>
                    {when && (
                        <div className={styles.tile} aria-hidden="true">
                            <span className={styles.tileMonth}>{when.month}</span>
                            <span className={styles.tileDay}>{when.day}</span>
                        </div>
                    )}
                    <div className={styles.facts}>
                        {when && (
                            <p className={styles.fact}>
                                <ClockIcon />
                                <time dateTime={when.iso}>
                                    {when.segments.map((segment, i) => (
                                        <span key={i} className={styles.segment}>
                                            {i > 0 && ' · '}
                                            {segment}
                                        </span>
                                    ))}
                                </time>
                            </p>
                        )}
                        {place && (
                            <p className={styles.fact}>
                                <PinIcon />
                                {mapUrl.trim() ? (
                                    <a {...linkProps({ type: 'url', url: mapUrl, newTab: true })}>
                                        {place}
                                    </a>
                                ) : (
                                    <span>{place}</span>
                                )}
                            </p>
                        )}
                    </div>
                </div>
            )}
            {body.trim() && <p className={parts.body}>{body}</p>}
            <Countdown countdown={countdown} target={target} className={layouts.afterText} />
            <Buttons buttons={buttons} className={layouts.afterText} />
        </>
    );

    if (doc.variant === 'image-left') {
        return (
            <SplitLayout image={image} onDismiss={onDismiss}>
                {content}
            </SplitLayout>
        );
    }
    if (doc.variant === 'image-background') {
        return (
            <OverlayLayout image={image} onDismiss={onDismiss}>
                {content}
            </OverlayLayout>
        );
    }
    return (
        <CardLayout image={image} onDismiss={onDismiss} align="start">
            {content}
        </CardLayout>
    );
}
